import { Box, Chip, MenuItem, Select, Stack, TextField } from "@mui/material";
import { Controller, RegisterOptions, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FiltersFormValues } from "../..";
import { FilterDTO } from "../../../../../../../../../../../../datasources/api-client/data-contracts";

type Props = {
	item: FilterDTO;
	index: number;
	form: UseFormReturn<FiltersFormValues>;
	rules?: RegisterOptions<FiltersFormValues, any>;
};

export const SelectNodeFilter = ({ item, index, form, rules }: Props) => {
	const { t } = useTranslation("common");
	const { control, setValue } = form;

	const isRequired = !!rules?.required;

	return (
		<Stack direction="row" spacing={2} alignItems="center">
			{/* label */}
			<TextField value={item.label} size="small" className="read-only" sx={{ flex: 1 }} />

			{/* operator */}
			<Controller
				name={`filters.${index}.operator`}
				control={control}
				render={({ field }) => {
					const currentOperator = field.value ?? item.operators?.[0]?.operator ?? "";

					return (
						<Select
							size="small"
							value={currentOperator}
							sx={{ flex: 1 }}
							onChange={(e) => {
								const newOp = e.target.value as string;

								field.onChange(newOp);

								// derive next state
								const nextIsMulti = newOp === "in" || newOp === "not in";

								setValue(`filters.${index}.value`, nextIsMulti ? [] : "");
							}}
						>
							{item.operators.map((op: any) => (
								<MenuItem key={op.operator} value={op.operator}>
									{op.label}
								</MenuItem>
							))}
						</Select>
					);
				}}
			/>

			{/* value */}
			<Controller
				name={`filters.${index}.value`}
				control={control}
				rules={rules}
				render={({ field, fieldState }) => {
					const currentOperator =
						form.getValues(`filters.${index}.operator`) ?? item.operators?.[0]?.operator ?? "";

					const selectedOperator =
						item.operators.find((o: any) => o.operator === currentOperator) ?? item.operators[0];

					const showValue = selectedOperator?.argsType !== "none";
					const isMulti = currentOperator === "in" || currentOperator === "not in";

					// normalize value
					const value = isMulti
						? Array.isArray(field.value)
							? field.value.map(String)
							: []
						: field.value
							? String(field.value)
							: "";

					return (
						<Select
							value={value}
							size="small"
							multiple={isMulti}
							disabled={!showValue}
							displayEmpty
							sx={{
								flex: 1,
								minWidth: 0,
								"& .MuiSelect-select": {
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								},
							}}
							error={!!fieldState.error}
							onChange={(e) => {
								const v = e.target.value;

								field.onChange(isMulti ? (Array.isArray(v) ? v : []).map(String) : String(v));
							}}
							renderValue={(selected) => {
								if (!selected || (Array.isArray(selected) && selected.length === 0)) {
									return isRequired ? `${t("select")} *` : t("select");
								}

								if (Array.isArray(selected)) {
									return (
										<Box sx={{ display: "flex", direction: "row", flexWrap: "wrap", gap: 0.25 }}>
											{selected.map((val) => {
												const option = item.options?.find((o: any) => String(o.id) === val);

												return (
													<Chip
														key={val}
														label={option?.label ?? val}
														color="primary"
														size="small"
													/>
												);
											})}
										</Box>
									);
								}

								const opt = item.options?.find((o: any) => String(o.id) === selected);

								return opt?.label ?? selected;
							}}
						>
							{item.options?.map((option: any) => (
								<MenuItem key={option.id} value={String(option.id)}>
									{option.label || option.id}
								</MenuItem>
							))}
						</Select>
					);
				}}
			/>
		</Stack>
	);
};
